import type { ColumnDef } from '@tanstack/react-table';
import { createActionsColumn, createSerialColumn } from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowActionButton } from '@/components/common/RowActions';
import { STATUS_COLORS } from '../constants/attendance.constants';
import type { JobCardItem } from '../types/attendance.types';

interface GetJobCardColumnsParams {
  searchText: string | undefined;
  onViewReport: (item: JobCardItem) => void;
}

export function getJobCardColumns({
  searchText,
  onViewReport,
}: GetJobCardColumnsParams): ColumnDef<JobCardItem, unknown>[] {
  return [
    createSerialColumn<JobCardItem>(),
    {
      accessorKey: 'amName',
      header: 'AM Name',
      cell: ({ getValue }) => {
        const amName = getValue() as string | null;
        return amName ? (
          <HighlightText text={amName} highlightedText={searchText} />
        ) : (
          <EmptyValue />
        );
      },
    },
    {
      accessorKey: 'zmName',
      header: 'ZM Name / ID (Cell)',
      cell: ({ row }) => {
        const { zmName, zmUsername, zmMobile } = row.original;
        if (!zmName) return <EmptyValue />;

        const idAndCell = [zmUsername, zmMobile === null ? null : `(${zmMobile})`]
          .filter(Boolean)
          .join(' ');

        return (
          <div className="min-w-0">
            <HighlightText text={zmName} highlightedText={searchText} />
            {idAndCell === '' ? null : (
              <span className="block text-xs tabular-nums text-muted-foreground">
                <HighlightText text={idAndCell} highlightedText={searchText} />
              </span>
            )}
          </div>
        );
      },
    },
    { accessorKey: 'thana', header: 'Thana', cell: ({ getValue }) => getValue() ?? <EmptyValue /> },
    {
      accessorKey: 'district',
      header: 'Zilla',
      cell: ({ getValue }) => getValue() ?? <EmptyValue />,
    },
    {
      accessorKey: 'division',
      header: 'Division',
      cell: ({ getValue }) => getValue() ?? <EmptyValue />,
    },
    {
      accessorKey: 'totalPresentDays',
      header: 'Total Present Days',
      cell: ({ getValue }) => (
        <span className={`${STATUS_COLORS.P.text} font-medium`}>{getValue() as number}</span>
      ),
    },
    {
      accessorKey: 'totalAbsentDays',
      header: 'Total Absent Days',
      cell: ({ getValue }) => (
        <span className={`${STATUS_COLORS.A.text} font-medium`}>{getValue() as number}</span>
      ),
    },
    { accessorKey: 'totalWeekends', header: 'Total Weekends' },
    { accessorKey: 'totalHolidays', header: 'Total Holidays' },
    { accessorKey: 'totalDays', header: 'Total Days' },
    {
      accessorKey: 'specimenQty',
      header: 'SP Qty',
      cell: ({ getValue }) => {
        const qty = getValue() as number | null;
        return qty === null ? <EmptyValue /> : <span className="tabular-nums">{qty}</span>;
      },
    },
    {
      accessorKey: 'visitedInstitute',
      header: 'Visited Inst.',
      cell: ({ getValue }) => {
        const visited = getValue() as number | null;
        return visited === null ? <EmptyValue /> : <span className="tabular-nums">{visited}</span>;
      },
    },
    createActionsColumn<JobCardItem>((jobCard) => (
      <RowActionButton
        label="View Report"
        ariaLabel={jobCard.zmName ? `View report of ${jobCard.zmName}` : 'View report'}
        onClick={() => onViewReport(jobCard)}
        isDisabled={jobCard.zmUserId == null}
      />
    )),
  ];
}
